import { useEffect, useState } from "react";
import { Truck, Loader2, CheckCircle, Navigation } from "lucide-react";
import { Card } from "@/components/ui/card";
import { getMyShipments, updateShipmentStatus } from "@/Calls/callTransportAuth";

interface Shipment {
    id: number; vehicle_vin: string | null; pickup_location: string | null;
    dropoff_location: string | null; status: string; scheduled_date: string | null;
    completed_date: string | null; created_at: string; dealer_id: number | null;
}

const STATUS_COLORS: Record<string, string> = {
    pending:    "bg-yellow-100 text-yellow-700",
    accepted:   "bg-blue-100 text-blue-700",
    in_transit: "bg-indigo-100 text-indigo-700",
    completed:  "bg-green-100 text-green-700",
    cancelled:  "bg-gray-100 text-gray-500",
};

const NEXT_STATUS: Record<string, { label: string; value: string; color: string }> = {
    pending:    { label: "Accept",          value: "accepted",   color: "text-blue-600"  },
    accepted:   { label: "Mark In Transit", value: "in_transit", color: "text-indigo-600"},
    in_transit: { label: "Mark Delivered",  value: "completed",  color: "text-green-600" },
};

export default function TransportShipmentsPage() {
    const [shipments,  setShipments]  = useState<Shipment[]>([]);
    const [loading,    setLoading]    = useState(true);
    const [filter,     setFilter]     = useState("all");
    const [updating,   setUpdating]   = useState<number | null>(null);

    const load = () => {
        setLoading(true);
        getMyShipments()
            .then((d) => setShipments(d.shipments ?? []))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const handleStatusUpdate = async (id: number, status: string) => {
        setUpdating(id);
        try {
            await updateShipmentStatus(id, status);
            load();
        } catch (e: any) {
            alert(e.response?.data?.message ?? "Update failed");
        } finally {
            setUpdating(null);
        }
    };

    const filtered = filter === "all" ? shipments : shipments.filter((s) => s.status === filter);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold">Shipments</h1>
                    <p className="text-muted-foreground text-sm mt-0.5">{shipments.length} total</p>
                </div>
                <div className="flex gap-2">
                    {["all", "pending", "accepted", "in_transit", "completed"].map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilter(s)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                filter === s
                                    ? "bg-blue-600 text-white"
                                    : "border border-border text-muted-foreground hover:border-blue-400"
                            }`}
                        >
                            {s === "all" ? "All" : s.replace("_", " ").toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-24">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            ) : filtered.length === 0 ? (
                <Card className="p-8 text-center text-muted-foreground">No shipments found</Card>
            ) : (
                <div className="space-y-3">
                    {filtered.map((s) => {
                        const next = NEXT_STATUS[s.status];
                        return (
                            <Card key={s.id} className="p-5">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
                                            <Truck className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium text-sm">Shipment #{s.id}</p>
                                                <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${STATUS_COLORS[s.status] ?? "bg-gray-100 text-gray-600"}`}>
                                                    {s.status.replace("_", " ").toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                                                <span>{s.pickup_location ?? "—"}</span>
                                                <Navigation className="h-3 w-3" />
                                                <span>{s.dropoff_location ?? "—"}</span>
                                            </div>
                                            {s.vehicle_vin && (
                                                <p className="text-xs text-muted-foreground mt-1">VIN: {s.vehicle_vin}</p>
                                            )}
                                            <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                                                {s.scheduled_date && <span>Scheduled: {new Date(s.scheduled_date).toLocaleDateString()}</span>}
                                                {s.completed_date && <span>Completed: {new Date(s.completed_date).toLocaleDateString()}</span>}
                                                <span>Created: {new Date(s.created_at).toLocaleDateString()}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {next && (
                                        <button
                                            onClick={() => handleStatusUpdate(s.id, next.value)}
                                            disabled={updating === s.id}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium hover:bg-muted transition-colors disabled:opacity-60 ${next.color}`}
                                        >
                                            {updating === s.id
                                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                                : <CheckCircle className="h-3 w-3" />
                                            }
                                            {next.label}
                                        </button>
                                    )}
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
